import React, { useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Box,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import colors from "../../../shared/constants/color";
import { textXS, textSM } from "../../../shared/constants/textStyles";
import { ThemeContext } from "../../../context/ThemeContextProvider";
import { getWiserTheme } from "../../../shared/theme/wiserTheme";

interface TableColumn {
  key: string;
  label: React.ReactNode;
  width?: string;
  maxWidth?: string;
  render?: (value: any, row: any) => React.ReactNode;
}

interface IProps {
  columns: TableColumn[];
  data: any[];
  emptyMessage: string;
  editable?: boolean;
  hideBorders?: boolean;
  theadBgColor?: string;
  theadTextColor?: string;
  py?: number;
  onRowClick?: (row: any) => void;
  onEditClick?: (row: any) => void;
  onDeleteClick?: (row: any) => void;
  actionCellSx?: SxProps<Theme>;
  borderColor?: string;
  headerBorderColor?: string;
  /** Satir bazli ek stil (upstream 367d4e0) -- "Toplam" satirini kalinlastirmak icin. */
  getRowSx?: (row: any) => SxProps<Theme>;
}

export const DataTable: React.FC<IProps> = ({
  columns,
  data,
  emptyMessage,
  editable = false,
  hideBorders = false,
  theadBgColor,
  theadTextColor,
  py = 1,
  onRowClick,
  onEditClick,
  onDeleteClick,
  actionCellSx,
  borderColor,
  headerBorderColor,
  getRowSx,
}) => {
  const { darkModeActive } = useContext(ThemeContext);
  const themeTokens = getWiserTheme(darkModeActive);
  const resolvedHeaderColor =
    theadTextColor ?? (darkModeActive ? "#FFFFFF" : colors.neutral.N200);
  const resolvedBorderColor =
    borderColor ?? (darkModeActive ? "#374151" : colors.neutral.N40);
  const resolvedHeaderBorderColor =
    headerBorderColor ?? resolvedBorderColor;
  const renderCellContent = (column: TableColumn, row: any) => {
    const value = row[column.key];

    if (column.render) {
      return column.render(value, row);
    }

    return (
      <Typography
        sx={textSM(
          400,
          darkModeActive ? themeTokens.textPrimary : colors.neutral.N700,
        )}
      >
        {value || ""}
      </Typography>
    );
  };

  return (
    <TableContainer>
      <Table size="small" sx={{ width: "100%" }}>
        <TableHead>
          <TableRow
            sx={{ ...(theadBgColor && { backgroundColor: theadBgColor }) }}
          >
            {columns.map((column, index) => (
              <TableCell
                key={index}
                sx={{
                  ...(column.width && { width: column.width }),
                  borderBottom: theadBgColor
                    ? "none"
                    : `2px solid ${resolvedHeaderBorderColor}`,
                  py: py,
                }}
              >
                {typeof column.label === "string" ? (
                  <Typography
                    sx={textXS(600, resolvedHeaderColor)}
                  >
                    {column.label}
                  </Typography>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
            {editable && (
              <TableCell
                sx={{
                  width: "60px",
                  borderBottom: theadBgColor
                    ? "none"
                    : `1px solid ${resolvedBorderColor}`,
                  py: py,
                  textAlign: "left",
                }}
              >
                <Typography
                  sx={textXS(600, resolvedHeaderColor)}
                >
                  İşlemler
                </Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.length ? (
            data.map((row: any, index: number) => (
              <TableRow
                key={index}
                onClick={() => onRowClick && onRowClick(row)}
                sx={{
                  ...(onRowClick && {
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: darkModeActive
                        ? "rgba(255, 255, 255, 0.04)"
                        : "rgba(0, 0, 0, 0.02)",
                    },
                  }),
                  ...(getRowSx ? getRowSx(row) : {}),
                }}
              >
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    sx={{
                      py: py,
                      ...(!hideBorders &&
                        resolvedBorderColor && {
                          borderBottom: `1px solid ${resolvedBorderColor}`,
                        }),
                      ...(hideBorders && { borderBottom: "none" }),
                      ...(column.maxWidth && { maxWidth: column.maxWidth }),
                    }}
                  >
                    {renderCellContent(column, row)}
                  </TableCell>
                ))}
                {editable && (
                  <TableCell
                    sx={{
                      py: py,
                      minWidth: "100px",
                      ...(!hideBorders &&
                        resolvedBorderColor && {
                          borderBottom: `1px solid ${resolvedBorderColor}`,
                        }),
                      ...(hideBorders && { borderBottom: "none" }),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "flex-start",
                        marginLeft: -1.3,
                        ...actionCellSx,
                      }}
                    >
                      {onEditClick && (
                        <IconButton
                          size="small"
                          sx={{ color: colors.gray500 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditClick(row);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {onDeleteClick && (
                        <IconButton
                          size="small"
                          sx={{
                            color: colors.error,
                            "&:hover": {
                              backgroundColor: "rgba(211, 47, 47, 0.04)",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Satıra tıklamayı engelle
                            onDeleteClick(row);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + (editable ? 1 : 0)}
                sx={{ textAlign: "center", py: 2 }}
              >
                <Typography sx={textSM(400, colors.gray500)}>
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Helper render functions for common cell types
export const renderTruncatedText =
  (maxWidth: string = "300px", lineClamp: number = 2) =>
  (value: string) => {
    if (!value) return "";
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: "13px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: lineClamp,
          WebkitBoxOrient: "vertical",
          maxWidth: maxWidth,
          lineHeight: 1.4,
        }}
        title={value}
      >
        {value}
      </Typography>
    );
  };

// KVKK Status Background Color Helper - Same as StatusTab
const getKvkkStatusBg = (status: string) => {
  switch (status) {
    case "Onaylandı":
      return "#E3FCEF";
    case "Reddedildi":
      return "#FFEBE6";
    case "Gönderildi":
      return "#FFFAE6";
    case "Gönderilmedi":
      return "#F4F5F7";
    default:
      return "#F4F5F7";
  }
};

export const renderKvkkChip = (value: any, row: any) => {
  // StatusTab ile aynı logic - kvkkApproved ve isKvkkSent boolean değerlerini kullan
  const statusText =
    row.kvkkApproved === true
      ? "Onaylandı"
      : row.kvkkApproved === false
        ? "Reddedildi"
        : row.isKvkkSent === true
          ? "Gönderildi"
          : "";

  return statusText ? (
    <Tooltip title="KVKK Durumu" arrow>
      <Chip
        label={statusText}
        size="small"
        sx={{
          backgroundColor: getKvkkStatusBg(statusText),
          color: "#172B4D",
          fontWeight: 500,
          fontSize: 12,
          border: "none",
          borderRadius: "4px",
        }}
      />
    </Tooltip>
  ) : (
    ""
  );
};

// StatusTab'daki gibi Alt Durum chip renderer.
// Satırda manuelScore varsa tooltip ile gösterilir (upstream 9bd8061).
export const renderSubStatusChip = (value: string, row?: any) => {
  if (!value) return "";

  const manuelScore = row?.manuelScore;
  const hasManuelScore = manuelScore !== null && manuelScore !== undefined;

  return (
    <Tooltip
      title={hasManuelScore ? `Manuel Skor: ${manuelScore}` : ""}
      disableHoverListener={!hasManuelScore}
      arrow
    >
      <Chip
        sx={{
          backgroundColor: "#FFEBE6",
          color: "#42526E",
          fontWeight: 600,
          fontSize: 12,
          borderRadius: "4px",
          border: "none",
        }}
        size="small"
        label={value || ""}
      />
    </Tooltip>
  );
};
